import { type RefObject, useEffect, useState } from 'react'

import type { CursorPosition } from './AnimatedCursor'

/** Matches the card's bundle CTA in both its states, so the cursor follows the flip. */
const CTA_LABEL = /^Add(ed)? to cart/

function findCta(root: HTMLElement): HTMLElement | null {
  const buttons = Array.from(root.querySelectorAll('button'))
  return buttons.find((button) => CTA_LABEL.test(button.textContent.trim())) ?? null
}

/**
 * Offset of `el` inside `root`, walking the offsetParent chain. Layout offsets are used
 * rather than client rects because the whole stage is CSS-scaled, and a rect would report
 * scaled pixels that then get applied as unscaled ones — landing the cursor short.
 */
function offsetWithin(el: HTMLElement, root: HTMLElement): CursorPosition | null {
  let x = 0
  let y = 0
  let node: HTMLElement | null = el
  while (node && node !== root) {
    x += node.offsetLeft
    y += node.offsetTop
    node = node.offsetParent as HTMLElement | null
  }
  // Ran past the root: the chain is not what this assumes, so don't guess a position.
  return node === root ? { x, y } : null
}

/**
 * Centre of the card's Add to cart button, in the card column's own coordinates.
 * Read from the rendered button rather than a fixed offset — the card grows a Remove
 * action once the quote is in the cart, which moves the CTA up the card.
 */
export function useCtaPosition(
  columnRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  recomputeKey: unknown
): CursorPosition | null {
  const [position, setPosition] = useState<CursorPosition | null>(null)

  useEffect(() => {
    const column = columnRef.current
    if (!enabled || !column) {
      setPosition(null)
      return
    }

    // The card is still settling its open animation when the scene flips, so measure next frame.
    const frame = requestAnimationFrame(() => {
      const cta = findCta(column)
      if (!cta) return
      const offset = offsetWithin(cta, column)
      if (!offset) return
      setPosition({ x: offset.x + cta.offsetWidth / 2, y: offset.y + cta.offsetHeight / 2 })
    })
    return () => cancelAnimationFrame(frame)
  }, [columnRef, enabled, recomputeKey])

  return position
}
